import Address from '@/models/Address';
import KeyValue from '@/models/KeyValue';
import ValidationLevelType from '@/enums/ValidationLevelType';
import ILookupValidationResponse from '@/models/interfaces/ILookupValidationResponse';
import { EnvironmentService } from './EnvironmentService';
import { store } from '../store';

export enum LookupResultEnum {
  Invalid = 4,
}

export class LookupService {
  private static serviceInstance: LookupService;

  private getCorrelationId(): string {
    return store.getters['initialSettingsModule/correlationId'];
  }

  public static get instance(): LookupService {
    if (!this.serviceInstance) {
      this.serviceInstance = new this();
    }
    return this.serviceInstance;
  }

  private get apiEnv(): string {
    return EnvironmentService.getEnv();
  }

  private get apiDomain(): string {
    return `https://px-func-lookup-${this.apiEnv}.azurewebsites.net/api`;
  }

  private get apiKeyParam(): string {
    let key = 'prod-a10e9350-0adf-48b5-8b57-2412ed34ba68';
    if (this.apiEnv === 'dev') {
      key = 'dev-key';
    }

    if (this.apiEnv === 'qa') {
      key = 'WQrp4Sb3uOt9lZ_jOcizrrDbhrgOndk4Yv_ItdQd6ek8AzFuXkVU7A==';
    }

    return `code=${key}`;
  }

  public async fetchSuggestions(hint: string): Promise<KeyValue[]> {
    return fetch(
      `${this.apiDomain}/address/suggestion?hint=${hint}&${this.apiKeyParam}`,
      {
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'GET'
      }
    )
      .then((response) => response.json())
      .then((json: any) => {
        if (json.value && json.isValid) {
          return json.value.map((item: any) => new KeyValue(item.udprn, item.suggestion));
        }

        return [];
      })
      .catch((exception: Error) => {
        window.console.error(
          'Failed to fetch GetAutocompletedAddressList GET.',
          {
            exception
          }
        );
      });
  }

  public async fetchAddress(udprn: string): Promise<Address> {
    return fetch(
      `${this.apiDomain}/address/${udprn}?${this.apiKeyParam}`,
      {
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'GET'
      }
    )
      .then((response) => response.json())
      .then((json: any) => {
        if (json.value && json.isValid) {
          const address = new Address();
          address.postcode = json.value.postcode ? json.value.postcode : '';
          address.buildingName = json.value.buildingName ? json.value.buildingName : '';
          address.buildingNumber = json.value.buildingNumber ? json.value.buildingNumber : '';
          address.subBuildingName = json.value.subBuildingName ? json.value.subBuildingName : '';
          address.street = json.value.thoroughFare ? json.value.thoroughFare : '';

          if (json.value.locality) {
            address.town = json.value.locality;
          } else if (json.value.dependentLocality) {
            address.town = json.value.dependentLocality;
          } else {
            address.town = '';
          }

          if (json.value.dependentLocality
            && address.town === json.value.dependentLocality) {
            address.dependentLocality = '';
          } else if (json.value.dependentLocality) {
            address.dependentLocality = json.value.dependentLocality;
          } else {
            address.dependentLocality = '';
          }

          address.subStreet = json.value.dependentThoroughFare ? json.value.dependentThoroughFare : '';
          address.organisationName = json.value.organisationName ? json.value.organisationName : '';
          address.latitude = json.value.latitude ? json.value.latitude : 0;
          address.longitude = json.value.longitude ? json.value.longitude : 0;

          if (address.buildingName.length === 0
            && address.buildingName.length === 0
            && address.organisationName.length > 0) {
            address.buildingName = address.organisationName;
            address.organisationName = '';
          }

          address.subNationalArea = json.value.subNationalArea ? json.value.subNationalArea : '';
          address.country = json.value.country ? json.value.country : '';

          return address;
        }

        return new Address();
      })
      .catch((error) => {
        throw error;
      });
  }

  private async validateUrl(url: string): Promise<ILookupValidationResponse> {
    try {
      const response = await fetch(url);
      const json = await response.json();
      return json as ILookupValidationResponse;
    } catch (error) {
      return this.lookupApiError;
    }
  }

  private get correlationIdKeyParam(): string {
    return `correlationId=${this.getCorrelationId()}`;
  }

  public async basicPhoneNumberCheck(phoneNumber: string): Promise<ILookupValidationResponse> {
    const url = this.getPhoneValidationUrl(phoneNumber, ValidationLevelType.Simple);
    return this.validateUrl(url);
  }

  public async activePhoneNumberCheck(phoneNumber: string): Promise<ILookupValidationResponse> {
    const url = this.getPhoneValidationUrl(phoneNumber, ValidationLevelType.Complete);
    return this.validateUrl(url);
  }

  public async activeEmailCheck(email: string, validationLevel: ValidationLevelType): Promise<ILookupValidationResponse> {
    const url = this.getEmailValidationUrl(email, validationLevel);
    return this.validateUrl(url);
  }

  private getPhoneValidationUrl(phoneNumber: string, validationLevel: ValidationLevelType): string {
    const validationLevelString = ValidationLevelType[validationLevel].toLowerCase();
    return `${this.apiDomain}/telephone/${phoneNumber}?validationlevel=${validationLevelString}&${this.apiKeyParam}&${this.correlationIdKeyParam}`;
  }

  private getEmailValidationUrl(email: string, validationLevel: ValidationLevelType): string {
    const validationLevelString = ValidationLevelType[validationLevel].toLowerCase();
    return `${this.apiDomain}/email?email=${email}&validationlevel=${validationLevelString}&${this.apiKeyParam}&${this.correlationIdKeyParam}`;
  }

  public async sendVerificationCode(telephoneNumber: string): Promise<ILookupValidationResponse> {
    const url = `${this.apiDomain}/telephone/verification/sendVerification?telephoneNumber=${telephoneNumber}&${this.apiKeyParam}&${this.correlationIdKeyParam}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const json = await response.json();
      return json as ILookupValidationResponse;
    } catch (error) {
      return this.lookupApiError;
    }
  }

  public async verifyTelephoneCode(telephoneNumber: string, verificationCode: string): Promise<ILookupValidationResponse> {
    const url = `${this.apiDomain}/telephone/verification/verify?telephoneNumber=${telephoneNumber}&verificationCode=${verificationCode}&${this.apiKeyParam}&${this.correlationIdKeyParam}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const json = await response.json();
      return json as ILookupValidationResponse;
    } catch (error) {
      return this.lookupApiError;
    }
  }

  private get lookupApiError(): ILookupValidationResponse {
    return {
      isValid: false,
      lookupResult: LookupResultEnum.Invalid,
      cacheExpiryDate: null,
      isFromCache: false,
      lookupResultName: '',
      messages: []
    };
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const lookupService = LookupService.instance;
